import ConstData from "./ConstData";
import { post_smarttv_media_trace } from "./api/hiclasstv";

const TRACE_ENABLED_KEY = "smartTVMediaTraceEnabled";
const TRACE_SESSION_KEY = "smartTVMediaTraceSessionId";
const MAX_EVENT_COUNT = 200;
const BATCH_EVENT_COUNT = 10;
const FLUSH_INTERVAL = 2000;

let isEnabled = false;
let traceSessionId = "";
let eventSeq = 0;
let batchSeq = 0;
let flushTimer = null;
let isSending = false;
let pendingBatch = null;
let events = [];
let context = {
    receiverSeq: -1,
    liveSeq: -1,
    pageType: ""
};
let status = {
    isRbmqServerConnected: false,
    isReadyLiveConsume: false,
    memberCount: 0,
    playingUsers: [],
    lastSuccessUserSeq: -1,
    lastFailedUserSeq: -1,
    lastStage: "",
    lastError: "",
    sentCount: 0,
    lastSentAt: "",
    lastAckAt: ""
};
const listeners = new Set();

const createTraceSessionId = () => `TV-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
const createEventId = () => {
    eventSeq += 1;
    return `${traceSessionId}-E${eventSeq}`;
};
const createBatchId = () => {
    batchSeq += 1;
    return `${traceSessionId}-B${batchSeq}`;
};
const getNow = () => new Date().toISOString();
const limitText = (value, maxLength) => value === undefined || value === null ? null : String(value).slice(0, maxLength);
const normalizeCode = value => typeof value === "number" ? value : limitText(value, 255);

const notify = () => {
    const snapshot = getSmartTVMediaTraceSnapshot();
    listeners.forEach(listener => {
        try {
            listener(snapshot);
        } catch (err) {
            console.log("[SMARTTV_MEDIA_TRACE] overlay listener error => ", err);
        }
    });
};

const sanitizeTraceEvent = (stage, fields = {}) => ({
    eventId: createEventId(),
    requestId: limitText(fields.requestId, 255),
    stage: limitText(stage, 80) || "UNKNOWN",
    prodUserSeq: Number.isInteger(fields.prodUserSeq) ? fields.prodUserSeq : null,
    consumUserSeq: Number.isInteger(fields.consumUserSeq) ? fields.consumUserSeq : null,
    roomKind: limitText(fields.roomKind, 255),
    mediaKind: limitText(fields.mediaKind, 255),
    timestamp: getNow(),
    errorCode: normalizeCode(fields.errorCode),
    errorStage: limitText(fields.errorStage, 255),
    code: normalizeCode(fields.code),
    message: limitText(fields.message || fields.errorMessage, 500),
    readyState: Number.isInteger(fields.readyState) ? fields.readyState : null,
    paused: typeof fields.paused === "boolean" ? fields.paused : null,
    videoWidth: Number.isInteger(fields.videoWidth) ? fields.videoWidth : null,
    videoHeight: Number.isInteger(fields.videoHeight) ? fields.videoHeight : null
});

const trimEventBuffer = () => {
    if (events.length <= MAX_EVENT_COUNT) return;

    const pendingIds = new Set(pendingBatch ? pendingBatch.events.map(event => event.eventId) : []);
    const removableIndex = events.findIndex(event => !pendingIds.has(event.eventId) && event.stage !== "VIDEO_ERROR" && !event.stage.endsWith("FAILED"));
    const fallbackIndex = events.findIndex(event => !pendingIds.has(event.eventId));
    const removeIndex = removableIndex >= 0 ? removableIndex : fallbackIndex;
    if (removeIndex >= 0) events.splice(removeIndex, 1);
};

const updateEventStatus = event => {
    status.lastStage = event.stage;
    if (event.stage === "VIDEO_PLAYING" && event.prodUserSeq !== null) {
        if (!status.playingUsers.includes(event.prodUserSeq)) status.playingUsers.push(event.prodUserSeq);
        status.lastSuccessUserSeq = event.prodUserSeq;
    }
    // 20260806 학생 화면앱 퇴장 시 현재 재생 인원에서 해당 학생만 제거한다.
    if (event.stage === "STUDENT_DISCONNECTED" && event.prodUserSeq !== null) {
        status.playingUsers = status.playingUsers.filter(userSeq => userSeq !== event.prodUserSeq);
    }
    if (event.stage === "VIDEO_ERROR" || event.stage.endsWith("FAILED")) {
        status.lastFailedUserSeq = event.prodUserSeq === null ? -1 : event.prodUserSeq;
        status.lastError = event.errorCode || event.message || event.code || event.stage;
    }
};

const createTracePayload = batchEvents => ({
    kind: "smartTVMediaTrace",
    action: "traceBatch",
    traceSessionId,
    batchId: createBatchId(),
    receiverSeq: Number.isInteger(context.receiverSeq) ? context.receiverSeq : -1,
    liveSeq: Number.isInteger(context.liveSeq) ? context.liveSeq : -1,
    pageType: String(context.pageType || "unknown").slice(0, 80),
    deviceInfo: {
        userAgent: window.navigator && window.navigator.userAgent ? window.navigator.userAgent.slice(0, 500) : "",
        screenWidth: window.screen ? window.screen.width : 0,
        screenHeight: window.screen ? window.screen.height : 0
    },
    events: batchEvents
});

// 260806 HTTP ACK가 확인된 진단 batch만 버퍼에서 제거하여 실기기 전송 실패를 보존한다.
export const flushSmartTVMediaTrace = async () => {
    if (!isEnabled || isSending || events.length === 0) return;

    if (!pendingBatch) pendingBatch = createTracePayload(events.slice(0, BATCH_EVENT_COUNT));
    isSending = true;
    status.lastSentAt = getNow();
    notify();

    try {
        const response = await post_smarttv_media_trace(pendingBatch);
        const result = response && response.data;
        if (result && result.isSuccessed === true && result.traceSessionId === traceSessionId && result.batchId === pendingBatch.batchId) {
            const acceptedIds = new Set(pendingBatch.events.map(event => event.eventId));
            events = events.filter(event => !acceptedIds.has(event.eventId));
            status.sentCount += result.acceptedCount || pendingBatch.events.length;
            status.lastAckAt = getNow();
            pendingBatch = null;
        } else {
            status.lastError = "TRACE_ACK_MISMATCH";
        }
    } catch (err) {
        status.lastError = err && err.message ? err.message : "TRACE_HTTP_FAILED";
    } finally {
        isSending = false;
        notify();
    }
};

// 260806 개발 빌드와 mediaTrace query가 모두 활성화된 경우에만 원격 진단을 시작한다.
export const initializeSmartTVMediaTrace = ({ search, pageType, liveSeq, receiverSeq }) => {
    try {
        const params = new URLSearchParams(search || "");
        const queryValue = params.get("mediaTrace");

        // 20260904 운영 빌드에서도 ?mediaTrace=Y 로 진단을 켤 수 있도록 개발 빌드 전용 차단을 주석 처리 ... by claude
        // if (!ConstData.IS_DEV_VALUE) {
        //     sessionStorage.removeItem(TRACE_ENABLED_KEY);
        //     sessionStorage.removeItem(TRACE_SESSION_KEY);
        //     isEnabled = false;
        //     notify();
        //     return;
        // }

        if (queryValue !== null) sessionStorage.setItem(TRACE_ENABLED_KEY, queryValue === "Y" ? "Y" : "N");
        isEnabled = sessionStorage.getItem(TRACE_ENABLED_KEY) === "Y";
        context = { pageType, liveSeq, receiverSeq };
        if (!isEnabled) {
            sessionStorage.removeItem(TRACE_SESSION_KEY);
            notify();
            return;
        }

        // 260806 mediaTrace=Y로 새로 진입한 경우에는 이전 접속과 구분되는 traceSessionId를 발급한다.
        if (queryValue === "Y") {
            traceSessionId = createTraceSessionId();
            eventSeq = 0;
            batchSeq = 0;
            events = [];
            pendingBatch = null;
            status = { ...status, playingUsers: [], lastSuccessUserSeq: -1, lastFailedUserSeq: -1, lastStage: "", lastError: "", sentCount: 0, lastSentAt: "", lastAckAt: "" };
        } else {
            traceSessionId = sessionStorage.getItem(TRACE_SESSION_KEY) || createTraceSessionId();
        }
        sessionStorage.setItem(TRACE_SESSION_KEY, traceSessionId);
        if (!flushTimer) flushTimer = setInterval(flushSmartTVMediaTrace, FLUSH_INTERVAL);
        notify();
    } catch (err) {
        // 260806 진단 저장소 접근 오류가 기존 SmartTV 화면 진입을 막지 않도록 비활성화한다.
        isEnabled = false;
        console.log("[SMARTTV_MEDIA_TRACE] initialize error => ", err);
    }
};

export const shutdownSmartTVMediaTrace = () => {
    if (isEnabled) flushSmartTVMediaTrace();
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = null;
};

export const isSmartTVMediaTraceEnabled = () => isEnabled;

export const updateSmartTVMediaTraceContext = nextContext => {
    context = { ...context, ...nextContext };
};

export const updateSmartTVMediaTraceStatus = nextStatus => {
    status = { ...status, ...nextStatus };
    notify();
};

export const recordSmartTVMediaTrace = (stage, fields) => {
    if (!isEnabled) return;
    try {
        const event = sanitizeTraceEvent(stage, fields);
        events.push(event);
        trimEventBuffer();
        updateEventStatus(event);
        notify();
        if (events.length >= BATCH_EVENT_COUNT) flushSmartTVMediaTrace();
    } catch (err) {
        // 260806 진단 오류는 기존 MediaSoup 성공 흐름에 영향을 주지 않는다.
        console.log("[SMARTTV_MEDIA_TRACE] record error => ", err);
    }
};

export const getSmartTVMediaTraceSnapshot = () => ({
    isEnabled,
    traceSessionId,
    bufferCount: events.length,
    pendingCount: pendingBatch ? pendingBatch.events.length : 0,
    isSending,
    ...status,
    playingCount: status.playingUsers.length
});

export const subscribeSmartTVMediaTrace = listener => {
    listeners.add(listener);
    listener(getSmartTVMediaTraceSnapshot());
    return () => listeners.delete(listener);
};
